/**
 * Thin wrappers around orval-generated Labels hooks.
 *
 * Labels are keyed by (team_id, trace_id) — there's one label per trace per
 * team, upserted via POST (the review composer is the only writer).
 */

import {
  keepPreviousData,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'

import type { ApiRequestError } from '@/api/client'
import {
  getListLabelsApiLabelsGetQueryKey,
  getListTagVocabularyApiLabelsTagsGetQueryKey,
  useDeleteTagApiLabelsTagsDeletePost,
  useListLabelsApiLabelsGet,
  useListTagVocabularyApiLabelsTagsGet,
  useRemoveFlagApiLabelsFlagByTraceTraceIdDelete,
  useRenameTagApiLabelsTagsRenamePost,
  useUpsertLabelApiLabelsPost,
  useUpsertTagsApiLabelsTagsPost,
  type upsertLabelApiLabelsPostResponse,
} from '@/api/generated/labels/labels'
import { getGetReviewQueueApiReviewQueueGetQueryKey } from '@/api/generated/review/review'
import {
  getListTrajectoriesApiTrajectoriesGetQueryKey,
  useListTrajectoriesApiTrajectoriesGet,
} from '@/api/generated/trajectories/trajectories'
import type {
  LabelResponse,
  LabelUpsert,
  ListTrajectoriesApiTrajectoriesGetParams,
  RunListItem,
  RunListResponse,
  TagDelete,
  TagRename,
  TagsUpsert,
  TagVocabularyResponse,
} from '@/api/generated/model'

export type Label = LabelResponse
export type { LabelUpsert, RunListItem, TagsUpsert }

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useLabels(): UnwrappedQuery<Label[]> {
  const query = useListLabelsApiLabelsGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * Invalidation shared by every write that changes a trace's VERDICT — the
 * review composer and the trajectories flag editor.
 *
 * All three surfaces render the same label row: the plain labels list, the
 * trajectories listing (its `flag`), and the review queue — a dismissal
 * saved on one page has to leave the queue on the other.
 */
function useLabelMutationInvalidation(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({
      queryKey: getListLabelsApiLabelsGetQueryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: getListTrajectoriesApiTrajectoriesGetQueryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: getGetReviewQueueApiReviewQueueGetQueryKey(),
    })
  }
}

export function useUpsertLabel(): UseMutationResult<
  upsertLabelApiLabelsPostResponse,
  ApiRequestError,
  { data: LabelUpsert }
> {
  const invalidate = useLabelMutationInvalidation()
  return useUpsertLabelApiLabelsPost({
    mutation: { onSuccess: invalidate },
  })
}

/**
 * Remove a trace's good/bad flag, keeping its tags (TRA-963).
 *
 * Destructive — the comment and the eval-PR stamp go with it. Dismissing
 * (an upsert with `review_status: 'dismissed'`) is the reversible option.
 */
export function useRemoveFlag(): UseMutationResult<
  unknown,
  ApiRequestError,
  { traceId: string }
> {
  const invalidate = useLabelMutationInvalidation()
  return useRemoveFlagApiLabelsFlagByTraceTraceIdDelete({
    mutation: { onSuccess: invalidate },
  })
}

export function useTagVocabulary(): UnwrappedQuery<TagVocabularyResponse> {
  const query = useListTagVocabularyApiLabelsTagsGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * The trajectories page listing. A page, sort or filter change keeps the
 * previous page on screen (dimmed) until the next one lands, so the table
 * never collapses and re-grows between fetches; `isFetching` says when
 * the rows shown are the previous request's, and `isPlaceholderData` says
 * the data itself is the previous request's (so an empty placeholder is
 * not a result).
 */
export function useTrajectories(
  params: ListTrajectoriesApiTrajectoriesGetParams,
): UnwrappedQuery<RunListResponse> & { isFetching: boolean; isPlaceholderData: boolean } {
  const query = useListTrajectoriesApiTrajectoriesGet(params, {
    query: { placeholderData: keepPreviousData },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * Replace the tag set on one trace. Invalidates every surface that renders
 * tags: the trajectories listing, the vocabulary (a new tag may have been
 * coined), the plain labels list, and the review queue — the composer's
 * TagEditor seeds from ReviewQueueItem.tags, and a stale cached set there
 * would be re-submitted wholesale on the next edit, wiping tags saved in
 * between.
 */
export function useUpsertTags(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TagsUpsert }
> {
  const queryClient = useQueryClient()
  return useUpsertTagsApiLabelsTagsPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListTrajectoriesApiTrajectoriesGetQueryKey(),
        })
        void queryClient.invalidateQueries({
          queryKey: getListTagVocabularyApiLabelsTagsGetQueryKey(),
        })
        void queryClient.invalidateQueries({
          queryKey: getListLabelsApiLabelsGetQueryKey(),
        })
        void queryClient.invalidateQueries({
          queryKey: getGetReviewQueueApiReviewQueueGetQueryKey(),
        })
      },
    },
  })
}

/** Shared invalidation for tag-management mutations (rename/delete). */
function useTagMutationInvalidation(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({
      queryKey: getListTagVocabularyApiLabelsTagsGetQueryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: getListTrajectoriesApiTrajectoriesGetQueryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: getListLabelsApiLabelsGetQueryKey(),
    })
    void queryClient.invalidateQueries({
      queryKey: getGetReviewQueueApiReviewQueueGetQueryKey(),
    })
  }
}

export function useRenameTag(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TagRename }
> {
  const invalidate = useTagMutationInvalidation()
  return useRenameTagApiLabelsTagsRenamePost({
    mutation: { onSuccess: invalidate },
  })
}

export function useDeleteTag(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TagDelete }
> {
  const invalidate = useTagMutationInvalidation()
  return useDeleteTagApiLabelsTagsDeletePost({
    mutation: { onSuccess: invalidate },
  })
}
